import React, { useEffect, useLayoutEffect, useRef, useState, useMemo } from 'react';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { toast } from "react-toastify";
import nodeApi from "../../nodeApi";
import CommonSelect from "../../core/common/commonSelect";
import TextareaAutosize from "react-textarea-autosize";

const INTERVIEW_TYPES = [
  { value: "Initial Screening", label: "Initial Screening" },
  { value: "Technical", label: "Technical" },
  { value: "Practical", label: "Practical" },
  { value: "HR", label: "HR" },
];

const CustomTimeInput = ({
  date,
  timeString,
  onChange,
}: {
  date: Date;
  timeString: string;
  onChange: (value: string) => void;
}) => {
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    const [hh, mm] = value.split(":").map(Number);
    if (isNaN(hh) || isNaN(mm)) return onChange(value);

    const newTime = new Date(date);
    newTime.setHours(hh, mm, 0, 0);
    const now = new Date();

    // Block past times when selecting today
    if (newTime.toDateString() === now.toDateString() && newTime < now) {
      return; // Ignore past time input
    }

    onChange(value);
  };

  return (
    <input
      type="time"
      value={timeString}
      onChange={handleChange}
      className="form-control"
      step={60}
    />
  );
};

 const fmtMonths = (m?: number) => {
    const n = Number(m) || 0;
    const y = Math.floor(n / 12);
    const r = n % 12;
    if (y && r) return `${y} year${y>1?'s':''} ${r} month${r>1?'s':''}`;
    if (y)      return `${y} year${y>1?'s':''}`;
    return `${r} month${r!==1?'s':''}`;
  };

interface ScheduleInterviewModalProps {
  show: boolean;
  onClose: () => void;
  jobId: number;
  jobProfile: string;
  interviewId?: number
  mode: string; // e.g., 'create', 'load', 'newRound'
  initialData?: {
    jobProfile?: string;
    candidateId?: number; // optional
    candidateName?: string;
    candidateEmail?: string;
    totalExperienceMonths?: number;
    currentCTC?: string;
    expectedCTC?: string;
    currentLocation?: string;
    resumeFileName?: string;
    [key: string]: any;
  };
  onSuccess?: () => void;
}

const ScheduleInterviewModal: React.FC<ScheduleInterviewModalProps> = ({
  show,
  onClose,
  jobId,
  jobProfile,
  interviewId,
  mode,
  initialData = {},
  onSuccess,
}) => {
  const [form, setForm] = useState<{
  candidateId?: number;
  candidateName: string;
  candidateEmail: string;
  totalExperienceMonths: number;
  currentCTC: string;
  expectedCTC: string;
  currentLocation: string;
  interviewType: string;
  preferredDate: string;
  resume: File | null;
  resumeFileName?: string;
  notes?: string;
  meetingLink: string;
  feedbackFormLink: string;
  interviewers: string[];
}>({
  candidateId: undefined,
  candidateName: "",
  candidateEmail: "",
  totalExperienceMonths: 0,
  currentCTC: "",
  expectedCTC: "",
  currentLocation: "",
  interviewType: "",
  preferredDate: "",
  resume: null,
  resumeFileName: "",
  notes: "",
  meetingLink: "",
  feedbackFormLink: "",
  interviewers: [],
});

  
    const [errors, setErrors] = useState<{
      candidateName?: string;
      candidateEmail?: string;
      totalExp?: string;
      currentCTC?: string;
      expectedCTC?: string;
      currentLocation?: string;
      interviewType?: string;
      preferredDate?: string;
      resume?: string;
      interviewers?: string;
      meetingLink?: string;
      feedbackFormLink?: string;
    }>({});
  

  const [isSaving, setIsSaving] = useState(false);


const resetInterviewForm = () => {
  setForm({
    candidateId: undefined,
    candidateName: "",
    candidateEmail: "",
    totalExperienceMonths: 0,
    currentCTC: "",
    expectedCTC: "",
    currentLocation: "",
    interviewType: "",
    preferredDate: "",
    resume: null,
    resumeFileName: "",
    notes: "",
    meetingLink: "",
    feedbackFormLink: "",
    interviewers: [],
  });
  setErrors({});
};

  useEffect(() => {
  if (show) {
    if ((mode === "load" || mode==="newRound") && initialData) {
      setForm((prev) => ({
      ...prev,
      ...initialData,
      currentCTC: String(initialData.currentCTC ?? "0"),
      expectedCTC: String(initialData.expectedCTC ?? "0"),
      totalExperienceMonths: initialData.totalExperienceMonths ?? 0,
    }));

      console.log("DATA=",initialData)
    } else {
      resetInterviewForm();
    }
  }
}, [show, mode, initialData]);

const [interviewers, setInterviewers] = useState<any[]>([]);

  const fetchInterviewers = async () => {
      try {
        const res = await nodeApi<any>(
          `/interviews/interviewers`
        );
        setInterviewers(res.data || []);
      } catch (err: any) {
        console.error("Fetch error:", err.message);
      }
    };
   const isInitialFetch = useRef(true);

    useEffect(() => {
         if (isInitialFetch.current) {
          isInitialFetch.current = false;
          fetchInterviewers();
          return;
        }
      }, []);

  const validateForm = () => {
    const errs: any = {};
    if (!form.candidateName) errs.candidateName = "Candidate Name is required.";
    if (!form.candidateEmail) {
      errs.candidateEmail = "Candidate Email is required.";
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(form.candidateEmail.trim())) {
        errs.candidateEmail = "Please enter a valid email address.";
      }
    }
    if (!form.resumeFileName && !form.resume) errs.resume = "Resume is required.";
    if (form.totalExperienceMonths === null || form.totalExperienceMonths === undefined)
    errs.totalExp = "Total Experience is required.";

    if (form.currentCTC === null || form.currentCTC === undefined || form.currentCTC === "")
      errs.currentCTC = "Current CTC is required.";

    if (form.expectedCTC === null || form.expectedCTC === undefined || form.expectedCTC === "")
      errs.expectedCTC = "Expected CTC is required.";

    if (!form.currentLocation) errs.currentLocation = "Current Location is required.";
    if (!form.preferredDate) errs.preferredDate = "Interview Date/Time is required.";
    if (!form.interviewType) errs.interviewType = "Interview Type is required.";
    if (!form.meetingLink) errs.meetingLink = "Meeting Link is required.";
    if (!form.feedbackFormLink) errs.feedbackFormLink = "Feedback Form Link is required.";
    if (!form.interviewers.length) errs.interviewers = "Assign at least one interviewer.";

    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSaveInterview = async () => {
    if (!validateForm()) return;

    try {
      setIsSaving(true);

      let candidateIdToUse = form.candidateId;
        const formData = new FormData();
        formData.append("name", form.candidateName);
        formData.append("email", form.candidateEmail);
        formData.append("total_experience_months", String(form.totalExperienceMonths));
        formData.append("ctc_info", form.currentCTC);
        formData.append("expected_ctc", form.expectedCTC);
        formData.append("current_location", form.currentLocation);
        formData.append("source", "Manually-HR");

        if (form.resume) formData.append("resume", form.resume);
        if (!form.resume && form.resumeFileName) formData.append("resume_file_name", form.resumeFileName);

        const res: any = await nodeApi(`/interviews/create-candidate`, { method: "POST", body: formData });
        if (!res?.data?.id) throw new Error("Failed to create candidate");
        candidateIdToUse = res.data.id;

        const body: any = {
        candidate_id: candidateIdToUse,
        job_opening_id: jobId,
        interview_type: form.interviewType,
        interviewers: form.interviewers,
        scheduled_datetime: form.preferredDate,
        meeting_link: form.meetingLink,
        feedback_form_link: form.feedbackFormLink,
        description: form.notes,
        };

        if (mode === "newRound" && interviewId) {
        body.id = interviewId;
        }

      await nodeApi(`/interviews/save-interview`, {
        method: "POST",
        body,
      });

      onClose();
      onSuccess?.();
    }  catch (err: any) {
            const backendMessage = err?.response?.data?.message;
            console.error("Error:", backendMessage);
            toast.error(backendMessage || "API failed");
      } finally {
      setIsSaving(false);
      resetInterviewForm()
    }
  };

  if (!show) return null;

  return (
    <div
          className="modal fade show d-block"
          tabIndex={-1}
          role="dialog"
          style={{ background: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content">
              {/* Header */}
              <div className="modal-header">
                <h4 className="modal-title">
                  {mode === "newRound" ? "Schedule Interview Round" : "Schedule Interview"}
                </h4>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    onClose()
                    resetInterviewForm();
                  }}
                />
              </div>

              <div className="modal-body">
                {/* Candidate Details */}
                <div className="card shadow-sm mb-4 border-0">
                  <div className="card-header bg-light">
                    <h5 className="mb-0">Candidate Details</h5>
                  </div>
                  <div className="card-body">
                    {/* Candidate Name & Email */}
                    <div className="row">
                      <div className="col-md-6 mb-3">
                        <label className="form-label">
                          Candidate Name <span className="text-danger">*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          value={form.candidateName}
                          onChange={(e) => {
                            setForm({
                              ...form,
                              candidateName: e.target.value,
                            })
                            setErrors((prev) => ({
                              ...prev,
                              candidateName: "",
                            }));
                          }}
                        />
                        {errors.candidateName && (
                          <small className="text-danger d-block mt-1">
                            {errors.candidateName}
                          </small>
                        )}
                      </div>

                      <div className="col-md-6 mb-3">
                        <label className="form-label">
                          Candidate Email <span className="text-danger">*</span>
                        </label>
                        <input
                          type="email"
                          className="form-control"
                          value={form.candidateEmail}
                          onChange={(e) => {
                          setForm({
                              ...form,
                              candidateEmail: e.target.value,
                            })
                            setErrors((prev) => ({
                              ...prev,
                              candidateEmail: "",
                            }));
                          }}
                          readOnly={mode === "newRound"}
                        />
                        {errors.candidateEmail && (
                          <small className="text-danger d-block mt-1">
                            {errors.candidateEmail}
                          </small>
                        )}
                      </div>
                    </div>

                    {/* Resume Upload */}
                    <div className="mb-3">
                      <label className="form-label">
                        Upload Resume <span className="text-danger">*</span>
                      </label>

                      {/* SHOW EXISTING FILE NAME IN LOAD MODE */}
                      {(mode === "load"||mode === "newRound") && form.resumeFileName && !form.resume && (
                        <div className="mb-2">
                          <a
                            href={form.resumeFileName}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-primary"
                          >
                            View Existing Resume
                          </a>
                        </div>
                      )}

                      {/* File upload field (still available in load mode) */}
                      <input
                        type="file"
                        accept=".pdf,.doc,.docx,.docs"
                        className="form-control"
                        onChange={(e) => {
                          const file = e.target.files?.[0] || null;

                          if (!file) {
                            setForm((s) => ({ ...s, resume: null }));
                            return;
                          }

                          const ext = file.name.substring(file.name.lastIndexOf(".")).toLowerCase();
                          const allowed = [".pdf", ".doc", ".docx", ".docs"];

                          if (!allowed.includes(ext)) {
                            setErrors((s) => ({
                              ...s,
                              resume: "Invalid file type. Allowed: PDF, DOC, DOCX, DOCS.",
                            }));
                            e.target.value = "";
                            return;
                          }

                          if (file.size > 5 * 1024 * 1024) {
                            setErrors((s) => ({
                              ...s,
                              resume: "File too large. Max size 5MB.",
                            }));
                            e.target.value = "";
                            return;
                          }

                          // If user uploads new file → replace and clear old name
                          setErrors((s) => ({ ...s, resume: "" }));
                          setForm((s) => ({
                            ...s,
                            resume: file,
                            resumeFileName: "", // clear old path
                          }));
                        }}
                      />

                      {/* If new resume uploaded */}
                      {form.resume && (
                        <small className="text-muted d-block mt-1">
                          Selected: {form.resume.name}
                        </small>
                      )}

                      {/* errors */}
                      {errors.resume && (
                        <small className="text-danger d-block mt-1">
                          {errors.resume}
                        </small>
                      )}
                    </div>

                    {/* Total Experience */}
                    <div className="mb-3">
                      <label className="form-label">
                        Total Experience <span className="text-danger">*</span>
                      </label>
                      <div className="d-flex gap-2">
                        {/* Years Input */}
                        <input
                          type="text"
                          inputMode="numeric"
                          pattern="[0-9]*"
                          className="form-control"
                          placeholder="Years"
                          value={
                           
                              Math.floor((form.totalExperienceMonths ?? 0 )/ 12).toString()
                              
                          }
                          onChange={(e) => {
                            const val = e.target.value;
                            if (!/^\d*$/.test(val)) return; // allow only digits

                            const years = parseInt(val || "0", 10);
                            const months = form.totalExperienceMonths % 12;
                            const total = years * 12 + months;

                            setForm((s) => ({
                              ...s,
                              totalExperienceMonths: total,
                            }));
                            setErrors((prev) => ({ ...prev, totalExp: "" }));
                          }}
                        />

                        {/* Months Input */}
                        <input
                          type="text"
                          inputMode="numeric"
                          pattern="[0-9]*"
                          className="form-control"
                          placeholder="Months"
                          value={
                            
                               ((form.totalExperienceMonths ?? 0) % 12).toString()
                             
                          }
                          onChange={(e) => {
                            const val = e.target.value;
                            if (!/^\d*$/.test(val)) return; // allow only digits

                            let months = parseInt(val || "0", 10);
                            if (months > 11) months = 11;
                            const years = Math.floor(form.totalExperienceMonths / 12);
                            const total = years * 12 + months;

                            setForm((s) => ({
                              ...s,
                              totalExperienceMonths: total,
                            }));
                            setErrors((prev) => ({ ...prev, totalExp: "" }));
                          }}
                        />
                      </div>

                      {/* Error */}
                      {errors.totalExp && (
                        <small className="text-danger d-block mt-1">
                          {errors.totalExp}
                        </small>
                      )}
                      <small className="text-muted d-block mt-1">
                        {fmtMonths(form?.totalExperienceMonths)}
                      </small>
                    </div>

                    {/* CTC */}
                    <div className="row">
                    {/* Current CTC */}
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Current CTC <span className="text-danger">*</span></label>
                      <input
                        type="text"
                        inputMode="decimal"
                        pattern="^\d*\.?\d*$"
                        className="form-control"
                        value={form.currentCTC}
                        onChange={(e) => {
                          const val = e.target.value;
                          // Allow only digits and one decimal point
                          if (!/^\d*\.?\d*$/.test(val)) return;
                          setForm({
                            ...form,
                            currentCTC: val,
                          });
                          setErrors((prev) => ({ ...prev, currentCTC: "" }));
                        }}
                      />
                      {errors.currentCTC && (
                        <small className="text-danger d-block mt-1">
                          {errors.currentCTC}
                        </small>
                      )}
                    </div>

                    {/* Expected CTC */}
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Expected CTC <span className="text-danger">*</span></label>
                      <input
                        type="text"
                        inputMode="decimal"
                        pattern="^\d*\.?\d*$"
                        className="form-control"
                        value={form.expectedCTC}
                        onChange={(e) => {
                          const val = e.target.value;
                          if (!/^\d*\.?\d*$/.test(val)) return;
                          setForm({
                            ...form,
                            expectedCTC: val,
                          });
                          setErrors((prev) => ({ ...prev, expectedCTC: "" }));
                        }}
                      />
                      {errors.expectedCTC && (
                        <small className="text-danger d-block mt-1">
                          {errors.expectedCTC}
                        </small>
                      )}
                    </div>
                  </div>

                    {/* Location */}
                    <div className="mb-3">
                      <label className="form-label">Current Location <span className="text-danger">*</span></label>
                      <input
                        type="text"
                        className="form-control"
                        value={form.currentLocation}
                        onChange={(e) => {
                          setForm({
                            ...form,
                            currentLocation: e.target.value,
                          })
                          setErrors((prev) => ({ ...prev, currentLocation: "" }));
                        }}
                      />
                      {errors.currentLocation && (
                          <small className="text-danger d-block mt-1">
                            {errors.currentLocation}
                          </small>
                        )}
                    </div>
                  </div>
                </div>

                {/* Interview Details */}
                <div className="card shadow-sm border-0">
                  <div className="card-header bg-light">
                    <h5 className="mb-0">Interview Details</h5>
                  </div>
                  <div className="card-body">
                    <div className="row">
                      {/* Position */}
                      <div className="col-md-6 mb-3">
                        <label className="form-label">
                          Position Applied For
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          value={jobProfile}
                          readOnly
                        />
                      </div>

                      {/* Preferred Date */}
                      <div className="col-md-6 mb-3">
                        <label className="form-label">
                          Preferred Interview Date & Time <span className="text-danger">*</span>
                        </label>

                        {(() => {
                          const selectedDate = form.preferredDate ? new Date(form.preferredDate) : null;
                          const now = new Date();

                          return (
                            <DatePicker
                              selected={selectedDate}
                              onChange={(date: Date | null) => {
                                setForm({
                                  ...form,
                                  preferredDate: date ? date.toISOString() : "",
                                });
                                setErrors((prev) => ({ ...prev, preferredDate: "" }));
                              }}
                              showTimeSelect
                              showTimeInput
                              dateFormat="dd-MMM-yyyy, hh:mm aa"
                              placeholderText="Select interview date & time"
                              className="form-control"
                              // Disable past dates
                              minDate={now}
                              // Disable past time only when selecting today
                              minTime={
                                selectedDate &&
                                selectedDate.toDateString() === now.toDateString()
                                  ? now
                                  : new Date(0, 0, 0, 0, 0, 0)
                              }
                              // Full day end time
                              maxTime={new Date(0, 0, 0, 23, 59, 59)}
                              customTimeInput={
                                <CustomTimeInput
                                  date={selectedDate ?? now}
                                  timeString={
                                    selectedDate
                                      ? selectedDate.toLocaleTimeString("en-GB", {
                                          hour: "2-digit",
                                          minute: "2-digit",
                                        })
                                      : ""
                                  }
                                  onChange={(value: string) => {
                                    if (!selectedDate) return;

                                    const [hh, mm] = value.split(":").map(Number);

                                    const updated = new Date(selectedDate);
                                    updated.setHours(hh, mm, 0, 0);

                                    const nowLocal = new Date();

                                    // Prevent selecting past times from the time list
                                    if (
                                      updated.toDateString() === nowLocal.toDateString() &&
                                      updated < nowLocal
                                    ) {
                                      return;
                                    }

                                    setForm({
                                      ...form,
                                      preferredDate: updated.toISOString(),
                                    });
                                  }}
                                />
                              }
                            />
                          );
                        })()}
                        {errors.preferredDate && (
                          <small className="text-danger d-block mt-1">
                            {errors.preferredDate}
                          </small>
                        )}
                      </div>
                    </div>

                    {/* Interview Type */}
                    <div className="mb-3">
                      <label className="form-label">
                        Interview Type <span className="text-danger">*</span>
                      </label>
                      <CommonSelect
                          options={INTERVIEW_TYPES}
                          value={
                            INTERVIEW_TYPES.find(
                              (opt) => opt.value === form.interviewType
                            ) || null
                          }
                          onChange={(selectedOption) => {
                            setForm({
                              ...form,
                              interviewType: selectedOption ? selectedOption.value : "",
                            });
                            setErrors({
                              ...errors,
                              interviewType: "",
                            });
                          }}
                          placeholder="Select interview type"
                          isHeight={true}
                          isDisabled={false}
                          forceFullWidth={true}
                        />
                      {errors.interviewType && (
                        <small className="text-danger d-block mt-1">
                          {errors.interviewType}
                        </small>
                      )}
                    </div>

                    {/* Interviewers */}
                    <div className="mb-3">
                      <div className="form-group">
                      <label className="form-label">
                        Assign Interviewer(s) <span className="text-danger">*</span>
                      </label>
                      <CommonSelect
                        isMulti={true}
                        options={interviewers.map((user) => ({
                          value: String(user.id),
                          label: user.name,
                        }))}
                        value={
                          form.interviewers
                            ? interviewers
                                .filter((u) =>
                                  form.interviewers.includes(String(u.id))
                                )
                                .map((u) => ({
                                  value: String(u.id),
                                  label: u.name,
                                }))
                            : null
                        }
                        onChange={(selected) => {
                          const selectedValues = Array.isArray(selected)
                            ? selected.map((opt) => opt.value)
                            : selected
                            ? [selected.value]
                            : [];
                          setForm({
                            ...form,
                            interviewers: selectedValues,
                          });
                          setErrors((prev) => ({ ...prev, interviewers: "" }));
                        }}
                        placeholder="Select interviewer(s)"
                        // isHeight={true}
                        forceFullWidth={true}
                      />
                      </div>
                      {errors.interviewers && (
                        <small className="text-danger d-block mt-1">
                          {errors.interviewers}
                        </small>
                      )}
                    </div>

                    {/* Meeting Link */}
                    <div className="mb-3">
                      <label className="form-label">Meeting Link <span className="text-danger">*</span></label>
                      <input
                        type="url"
                        className="form-control"
                        value={form.meetingLink}
                        onChange={(e) => {
                          setForm({
                            ...form,
                            meetingLink: e.target.value,
                          })
                          setErrors((prev) => ({ ...prev, meetingLink: "" }));
                        }}
                      />
                      {errors.meetingLink && (
                        <small className="text-danger d-block mt-1">
                          {errors.meetingLink}
                        </small>
                      )}
                    </div>

                    {/* Feedback Form Link */}
                    <div className="mb-3">
                      <label className="form-label">
                        Feedback Form Link <span className="text-danger">*</span>
                      </label>
                      <input
                        type="url"
                        className="form-control"
                        value={form.feedbackFormLink}
                        onChange={(e) => {
                          setForm({
                            ...form,
                            feedbackFormLink: e.target.value,
                          })
                         setErrors((prev) => ({ ...prev, feedbackFormLink: "" }));
                        }}
                      />
                      {errors.feedbackFormLink && (
                        <small className="text-danger d-block mt-1">
                          {errors.feedbackFormLink}
                        </small>
                      )}
                    </div>

                    {/* Notes */}
                    <div className="mb-3">
                      <label className="form-label">Short Description / Screening Notes</label>
                      <TextareaAutosize
                        className="form-control"
                        minRows={4}
                        maxRows={10}
                        value={form.notes}
                        onChange={(e) =>
                          setForm({
                            ...form,
                            notes: e.target.value,
                          })
                        }
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="modal-footer">
                <button
                  className="btn btn-light me-2"
                  onClick={() => {
                    onClose()
                    resetInterviewForm();
                  }}
                >
                  Cancel
                </button>
                <button className="btn btn-primary" onClick={handleSaveInterview} disabled={isSaving} >
                  {isSaving ? "Saving..." : "Save"}
                </button>
              </div>
            </div>
          </div>
        </div>
  );
};

export default ScheduleInterviewModal;
